import {
  COVER_BLOB_POSITION_JITTER,
  COVER_BLOB_ROLES,
  COVER_HUE_DRIFT_DEGREES,
  COVER_PALETTES,
} from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/constants";
import { createSeededRandom } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/createSeededRandom";
import { formatOklchColor } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/formatOklchColor";
import { hashCoverSeed } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/hashCoverSeed";
import type { ProjectCover } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/types";

const FULL_TURN_DEGREES = 360;
const GRAIN_SEED_RANGE = 1000;
const COORDINATE_DECIMALS = 2;
const MIN_BLOB_WIDTH_SCALE = 0.85;
const MIN_BLOB_HEIGHT_SCALE = 0.7;
const BLOB_SCALE_RANGE = 0.4;

const roundCoordinate = (coordinate: number) =>
  Number(coordinate.toFixed(COORDINATE_DECIMALS));

// The cover a project or template wears in the library, derived from its id alone: nothing is stored or uploaded, the same row always gets the same orb, and the bank is as large as the id space.
export const buildProjectCover = (seedText: string): ProjectCover => {
  const random = createSeededRandom(hashCoverSeed(seedText));
  const palette = COVER_PALETTES[Math.floor(random() * COVER_PALETTES.length)];
  const hueDriftDegrees =
    random() * COVER_HUE_DRIFT_DEGREES * 2 - COVER_HUE_DRIFT_DEGREES;
  const rotationDegrees = Math.floor(random() * FULL_TURN_DEGREES);

  const blobs = COVER_BLOB_ROLES.map((blobRole) => {
    const offsetX =
      random() * COVER_BLOB_POSITION_JITTER * 2 - COVER_BLOB_POSITION_JITTER;
    const offsetY =
      random() * COVER_BLOB_POSITION_JITTER * 2 - COVER_BLOB_POSITION_JITTER;
    const widthScale = MIN_BLOB_WIDTH_SCALE + random() * BLOB_SCALE_RANGE;
    const heightScale = MIN_BLOB_HEIGHT_SCALE + random() * BLOB_SCALE_RANGE;
    return {
      centerX: roundCoordinate(blobRole.homeX + offsetX),
      centerY: roundCoordinate(blobRole.homeY + offsetY),
      radiusX: roundCoordinate(blobRole.radius * widthScale),
      radiusY: roundCoordinate(blobRole.radius * heightScale),
      color: formatOklchColor(palette[blobRole.role], hueDriftDegrees),
      opacity: blobRole.opacity,
    };
  });

  return {
    bodyColor: formatOklchColor(palette.body, hueDriftDegrees),
    // The main colour, not the body: the body is the darker base coat and glows muddy, while the main colour is what the eye reads the orb as.
    glowColor: formatOklchColor(palette.main, hueDriftDegrees),
    blobs,
    rotationDegrees,
    grainSeed: Math.floor(random() * GRAIN_SEED_RANGE),
  };
};
